import React from "react";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { translations } from "@/lib/i18n";
import { isChunkLoadError, markChunkFailedAgain, mayReloadForChunk, reloadOnceForChunkError } from "@/lib/chunkReload";   // Batch 10 · B16; STEP 10 · C2

/**
 * Global error boundary — catches uncaught render errors so the whole
 * app never shows a raw stack trace overlay to end users.
 *
 * Note: uses a manual lang read (localStorage) + direct dict lookup
 * because a class component can't call the useI18n() hook, and the
 * boundary MUST keep working even if the I18nProvider itself throws.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(error) {
    // Batch 10 · B16: a chunk from before a release is gone — the page reloads once (componentDidCatch) instead of the error screen
    if (mayReloadForChunk(error)) return { hasError: false, reloading: true, message: "" };
    return { hasError: true, message: (error && error.message) || "Unexpected error" };
  }
  componentDidCatch(error, info) {
    if (reloadOnceForChunkError(error)) return;     // Batch 10 · B16: reloading — nothing to report (STEP 10: reported after the reload, as recovered)
    if (isChunkLoadError(error)) markChunkFailedAgain();   // STEP 10 · C2: the reload did not cure it → an ordinary (pending) report below
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    try {
      const payload = {
        message: String((error && error.message) || "unknown"),
        name: String((error && error.name) || "Error"),
        stack: String((error && error.stack) || "").slice(0, 4000),
        component_stack: String((info && info.componentStack) || "").slice(0, 4000),
        pathname: typeof window !== "undefined" ? window.location?.pathname : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        at: new Date().toISOString(),
      };
      api.post("/client-errors", payload, { _silent: true }).catch(() => {});
    } catch {
      /* swallow */
    }
  }
  handleReset = () => {
    this.setState({ hasError: false, message: "" });
    if (typeof window !== "undefined") window.location.reload();
  };
  render() {
    if (this.state.reloading) {
      return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="chunk-reloading">…</div>;
    }
    if (this.state.hasError) {
      const lang = (typeof localStorage !== "undefined" && localStorage.getItem("af_lang")) || "en";
      const eb = translations[lang]?.errorBoundary || translations.en.errorBoundary;
      return (
        <div className="flex min-h-[60vh] items-center justify-center p-8" data-testid="error-boundary">
          <div className="w-full max-w-lg rounded-lg border border-red-200 bg-red-50 p-6 text-center dark:bg-red-950/40 dark:border-red-900">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h2 className="mt-3 text-lg font-semibold text-red-900 dark:text-red-100">
              {eb.title}
            </h2>
            <p className="mt-1 text-sm text-red-800 dark:text-red-200">
              {this.state.message || eb.pageCannotLoad}
            </p>
            <button
              onClick={this.handleReset}
              className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              data-testid="error-reload"
            >
              {eb.reload}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
